import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { EiqInput, EiqCheckbox, EiqButton } from 'eiq-storybook';

import { RESTORE_PASSWORD } from 'src/routes/routes';
import Styled from 'src/layouts/PublicLayout/PublicLayout.styles';
import s from 'src/components/SignInForm/SignInForm.styles';

const SignInForm = props => {
  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    className,
    logInError,
    setFieldValue,
    touched
  } = props;
  const { t } = useTranslation();
  const formStyles = cn('form -white', className);
  const [passwordShown, setPasswordShown] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const togglePasswordVisiblity = () => {
    setPasswordShown(!passwordShown);
  };

  const onBlurWithTrim = e => {
    const newEvent = { ...e };
    newEvent.target.value = newEvent.target.value.trim();
    handleChange(newEvent);
    handleBlur(newEvent);
  }

  const onKeyDownWithTrim = e => {
    if (e.key === 'Enter') {
      onBlurWithTrim(e);
    }
  }

  useLayoutEffect(()=>{
    if(emailRef.current){
      emailRef.current.setAttribute('required','')
    }
  },[emailRef.current]);

  useLayoutEffect(()=>{
    if(passwordRef.current){
      passwordRef.current.setAttribute('required','')
    }
  },[passwordRef.current])

  const onReminderChange = () => {
    setFieldValue('rememberMe', !values.rememberMe);
    handleChange();
  }

  return (
    <form className={formStyles} onSubmit={handleSubmit} noValidate="noValidate" >
      <Styled.Header />
      <s.FormError>{logInError && <s.TextFieldError>{logInError}</s.TextFieldError>}</s.FormError>
      <s.RequiredFieldLabel>indicates a required field</s.RequiredFieldLabel>
      <div className="form-row form-row__email">
        <EiqInput
          errorMessage={touched.email && errors.email}
          id="email"
          label={t('FORM_LOGIN_EMAIL')}
          name="email"
          onChange={handleChange}
          onBlur={onBlurWithTrim}
          placeholder={'Enter your email here'}
          value={values.email}
        />
      </div>

      <div className="form-row form-row__password">
        <EiqInput
            errorMessage={touched.password && errors.password}
            id="password"
            label={t('FORM_PASSWORD')}
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={'Enter your password here'}
            type={passwordShown ? 'text' : 'password'}
            value={values.password}
          />
        <s.ShowButton link onClick={togglePasswordVisiblity} sm type="button" >
          {passwordShown ? t('HIDE') : t('SHOW')}
        </s.ShowButton>
      </div>
      <div className="form-row -inline">
        <div className="form-col text-left">
          <s.CheckBoxContainer>
            <EiqCheckbox
              ariaLabel={t('REMEMBER_ME')}
              checked={values.rememberMe}
              id="rememberMe"
              label={t('REMEMBER_ME')}
              onChange={ onReminderChange }
              value={values.rememberMe}
            /> 
          </s.CheckBoxContainer>
        </div>
        <div className="form-col -auto">
          <Link className="login-link" to={RESTORE_PASSWORD}>{t('FORGOT_LINK')}</Link>
        </div>
      </div>

      <div className="form-row -centered">
        <EiqButton 
          type="submit"
          aria-label="login"
          id="btnSubmit"
          label={isSubmitting ? t('PLEASE_WAIT') : t('SIGN_IN')}
          size="btn--sm"
          variant="btn--outline-link"
          variantColor="darkMint-100"
          className="loginBtn"
          disabled={isSubmitting} />
      </div>

      <Styled.Footer />
    </form>
  );
};

SignInForm.propTypes = {
  values: PropTypes.shape({
    email: PropTypes.string,
    password: PropTypes.string,
    rememberMe: PropTypes.bool,
  }).isRequired,
  touched: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({
    email: PropTypes.string,
    password: PropTypes.string,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  logInError: PropTypes.string,
};

SignInForm.defaultProps = {
  className: null,
};

export default SignInForm;
